import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import LineChart from './chart/LineChart';
import DateChange from './chart/DateChange';
import AdminLayout from "./component/AdminLayout";

const Admin = () => {

    // 작가 등록 회원 수 (Author)
    const [authorChartType, setAuthorChartType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
    const [authorSelectedDate, setAuthorSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);

    // 평균 체류시간
    const [visitorChartType, setVisitorChartType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
    const [visitorSelectedDate, setVisitorSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);

    // 방문 회원 수 (Session)
    const [sessionChartType, setSessionChartType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
    const [sessionSelectedDate, setSessionSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);

    // 접속자 수
    const [signUpChartType, setSignUpChartType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
    const [signUpSelectedDate, setSignUpSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);

    const [authorData, setAuthorData] = useState<number[]>([]);
    const [authorLabels, setAuthorLabels] = useState<string[]>([]);

    const [visitorData, setVisitorData] = useState<number[]>([]);
    const [visitorLabels, setVisitorLabels] = useState<string[]>([]);

    const [sessionData, setSessionData] = useState<number[]>([]);
    const [sessionLabels, setSessionLabels] = useState<string[]>([]);

    const [signUpData, setSignUpData] = useState<number[]>([]);
    const [signUpLabels, setSignUpLabels] = useState<string[]>([]);

    // 소셜 가입 추이
    const [socialSignupChartType, setSocialSignupChartType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
    const [socialSignupSelectedDate, setSocialSignupSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [socialSignupData, setSocialSignupData] = useState<number[]>([]);
    const [socialSignupLabels, setSocialSignupLabels] = useState<string[]>([]);

    // API New Data
    const [summary, setSummary] = useState<any>(null);

    // 토큰을 ref로 관리 — setState 하지 않아 리렌더 없음
    const adminTokenRef = useRef<string | null>(null);

    const getAdminToken = useCallback(async (): Promise<string | null> => {
        return sessionStorage.getItem('adminToken');
    }, []);

    const parseLabelsAndData = (
        data: any[],
        type: 'daily' | 'weekly' | 'monthly',
        valueKey: string
    ) => {
        let labels: string[] = [];
        let values: number[] = [];
        if (type === 'daily') {
            labels = data.map((item) => item.date);
        } else if (type === 'monthly') {
            labels = data.map((item) => item.month);
        } else if (type === 'weekly') {
            labels = data.map((item) => `${item.startDate}~${item.endDate}`);
        }
        values = data.map((item) => item[valueKey]);
        return { labels, values };
    };

    const fetchAuthorData = useCallback(async (type: 'daily' | 'weekly' | 'monthly', date: string) => {
        const token = await getAdminToken();
        if (!token) return;
        const params = {
            daily: { type: 'daily', date },
            weekly: { type: 'weekly', endDate: date },
            monthly: { type: 'monthly', month: date.substring(0, 7) },
        };
        try {
            const response = await axios.post('/api/admin/authors/registrations', params[type], {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (Array.isArray(response.data)) {
                const { labels, values } = parseLabelsAndData(response.data, type, 'authorCount');
                setAuthorLabels(labels);
                setAuthorData(values);
            }
        } catch (error) {
            console.error('Error fetching author data:', error);
        }
    }, [getAdminToken]);

    const fetchVisitorData = useCallback(async (type: 'daily' | 'weekly' | 'monthly', date: string) => {
        const token = await getAdminToken();
        if (!token) return;
        const params = {
            daily: { type: 'daily', date },
            weekly: { type: 'weekly', endDate: date },
            monthly: { type: 'monthly', month: date.substring(0, 7) },
        };
        try {
            const response = await axios.post('/api/admin/metrics/duration', params[type], {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (Array.isArray(response.data)) {
                const { labels, values } = parseLabelsAndData(response.data, type, 'averageDurationSeconds');
                setVisitorLabels(labels);
                setVisitorData(values);
            }
        } catch (error) {
            console.error('Error fetching visitor data:', error);
        }
    }, [getAdminToken]);

    const fetchSessionData = useCallback(async (type: 'daily' | 'weekly' | 'monthly', date: string) => {
        const token = await getAdminToken();
        if (!token) return;
        const params = {
            daily: { type: 'daily', date },
            weekly: { type: 'weekly', endDate: date },
            monthly: { type: 'monthly', month: date.substring(0, 7) },
        };
        try {
            const response = await axios.post('/api/admin/users/visitors', params[type], {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (Array.isArray(response.data)) {
                const { labels, values } = parseLabelsAndData(response.data, type, 'visitorCount');
                setSessionLabels(labels);
                setSessionData(values);
            }
        } catch (error) {
            console.error('Error fetching session data:', error);
        }
    }, [getAdminToken]);

    const fetchSignUpData = useCallback(async (type: 'daily' | 'weekly' | 'monthly', date: string) => {
        const token = await getAdminToken();
        if (!token) return;
        const params = {
            daily: { type: 'daily', date },
            weekly: { type: 'weekly', endDate: date },
            monthly: { type: 'monthly', month: date.substring(0, 7) },
        };
        try {
            const response = await axios.post('/api/admin/all/connections', params[type], {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (Array.isArray(response.data)) {
                const { labels, values } = parseLabelsAndData(response.data, type, 'connectionCount');
                setSignUpLabels(labels);
                setSignUpData(values);
            }
        } catch (error) {
            console.error('Error fetching sign-up data:', error);
        }
    }, [getAdminToken]);

    const fetchSocialSignupData = useCallback(async (type: 'daily' | 'weekly' | 'monthly', date: string) => {
        const token = await getAdminToken();
        if (!token) return;
        const params = {
            daily: { type: 'daily', date },
            weekly: { type: 'weekly', endDate: date },
            monthly: { type: 'monthly', month: date.substring(0, 7) },
        };
        try {
            const response = await axios.post('/api/admin/users/social-signups', params[type], {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (Array.isArray(response.data)) {
                const { labels, values } = parseLabelsAndData(response.data, type, 'signupCount');
                setSocialSignupLabels(labels);
                setSocialSignupData(values);
            }
        } catch (error) {
            console.error('Error fetching social signup data:', error);
        }
    }, [getAdminToken]);


    useEffect(() => {
        fetchAuthorData(authorChartType, authorSelectedDate);
    }, [authorChartType, authorSelectedDate, fetchAuthorData]);

    useEffect(() => {
        fetchVisitorData(visitorChartType, visitorSelectedDate);
    }, [visitorChartType, visitorSelectedDate, fetchVisitorData]);

    useEffect(() => {
        fetchSessionData(sessionChartType, sessionSelectedDate);
    }, [sessionChartType, sessionSelectedDate, fetchSessionData]);

    useEffect(() => {
        fetchSignUpData(signUpChartType, signUpSelectedDate);
    }, [signUpChartType, signUpSelectedDate, fetchSignUpData]);

    useEffect(() => {
        fetchSocialSignupData(socialSignupChartType, socialSignupSelectedDate);
    }, [socialSignupChartType, socialSignupSelectedDate, fetchSocialSignupData]);


    useEffect(() => {
        const fetchDashboardData = async () => {
            const token = await getAdminToken();
            if (!token) return;
            try {
                const sumRes = await axios.post('/api/admin/dashboard/summary', {}, { headers: { Authorization: `Bearer ${token}` } });
                setSummary(sumRes.data);
            } catch (error) {
                console.error("Failed to fetch dashboard summary", error);
            }
        };
        fetchDashboardData();
    }, [getAdminToken]);


    const formatSecondsToMinSec = (seconds: number) => {
        if (!seconds || isNaN(seconds)) return '0초';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        if (m > 0) return `${m}분 ${s}초`;
        return `${s}초`;
    };

    return (
        <AdminLayout
            pageTitle="Dashboard"
            breadcrumbs={[
                { label: 'Home', link: '/admin' },
                { label: 'Dashboard', active: true }
            ]}
        >

            {/* Main Content */}
            <div className="main-content">
                {summary && (
                    <div className="row mb-4">
                        {[
                            { title: '방문 회원 수', data: summary.visitors },
                            { title: '작가 등록 회원 수', data: summary.registrations },
                            { title: '접속자 수', data: summary.connections },
                            { title: '평균 체류시간', data: summary.avgDurationMin, isDuration: true }
                        ].map((stat, idx) => (
                            <div key={idx} className="col-12 col-sm-6 col-xl-3 mb-4 mb-xl-0">
                                <div className="card border-0 shadow-sm rounded-4 h-100">
                                    <div className="card-body p-4">
                                        <h6 className="text-muted fw-medium mb-1">{stat.title}</h6>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h3 className="fw-bold mb-0 text-dark">
                                                {stat.isDuration ? formatSecondsToMinSec(stat.data?.today || 0) : (stat.data?.today?.toLocaleString() ?? 0)}
                                            </h3>
                                            <div className={`d-flex align-items-center px-2 py-1 rounded-pill small ${stat.data?.diff >= 0 ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'}`} style={{ backgroundColor: stat.data?.diff >= 0 ? '#ecfdf5' : '#fef2f2' }}>
                                                <i className={`feather-${stat.data?.diff >= 0 ? 'arrow-up-right' : 'arrow-down-right'} me-1`}></i>
                                                <span className="fw-bold">{Math.abs(stat.data?.diff || 0)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="row">
                    {/* 차트 데이터 */}
                    <div className="col-12 col-xl-6 mb-4">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
                            <div className="card-header bg-white border-0 pt-4 pb-0">
                                <h5 className="card-title fw-bold text-dark mb-0">작가 등록 회원 수</h5>
                            </div>
                            <DateChange
                                selectedDate={authorSelectedDate}
                                setSelectedDate={setAuthorSelectedDate}
                                chartType={authorChartType}
                                setChartType={setAuthorChartType}
                                handleDateChange={(e) => setAuthorSelectedDate(e.target.value)}
                            />
                            <div className="card-body p-4 pt-0">
                                <LineChart data={authorData} labels={authorLabels} title="작가 등록 회원 수" color="#6366f1" />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-6 mb-4">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
                            <div className="card-header bg-white border-0 pt-4 pb-0">
                                <h5 className="card-title fw-bold text-dark mb-0">평균 체류시간 (초)</h5>
                            </div>
                            <DateChange
                                selectedDate={visitorSelectedDate}
                                setSelectedDate={setVisitorSelectedDate}
                                chartType={visitorChartType}
                                setChartType={setVisitorChartType}
                                handleDateChange={(e) => setVisitorSelectedDate(e.target.value)}
                            />
                            <div className="card-body p-4 pt-0">
                                <LineChart data={visitorData} labels={visitorLabels} title="평균 체류시간" color="#14b8a6" isDuration={true} />
                            </div>
                        </div>
                    </div>

                    {/* 방문 회원 수 */}
                    <div className="col-12 col-xl-6 mb-4">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
                            <div className="card-header bg-white border-0 pt-4 pb-0">
                                <h5 className="card-title fw-bold text-dark mb-0">방문 회원 수</h5>
                            </div>
                            <DateChange
                                selectedDate={sessionSelectedDate}
                                setSelectedDate={setSessionSelectedDate}
                                chartType={sessionChartType}
                                setChartType={setSessionChartType}
                                handleDateChange={(e) => setSessionSelectedDate(e.target.value)}
                            />
                            <div className="card-body p-4 pt-0">
                                <LineChart data={sessionData} labels={sessionLabels} title="방문 회원 수" color="#0ea5e9" />
                            </div>
                        </div>
                    </div>

                    {/* 접속자 수 */}
                    <div className="col-12 col-xl-6 mb-4">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
                            <div className="card-header bg-white border-0 pt-4 pb-0">
                                <h5 className="card-title fw-bold text-dark mb-0">접속자 수</h5>
                            </div>
                            <DateChange
                                selectedDate={signUpSelectedDate}
                                setSelectedDate={setSignUpSelectedDate}
                                chartType={signUpChartType}
                                setChartType={setSignUpChartType}
                                handleDateChange={(e) => setSignUpSelectedDate(e.target.value)}
                            />
                            <div className="card-body p-4 pt-0">
                                <LineChart data={signUpData} labels={signUpLabels} title="접속자 수" color="#f59e0b" />
                            </div>
                        </div>
                    </div>

                    {/* 소셜 가입 추이 */}
                    <div className="col-12 col-xl-6 mb-4">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
                            <div className="card-header bg-white border-0 pt-4 pb-0">
                                <h5 className="card-title fw-bold text-dark mb-0">소셜 가입 추이 (일반유저)</h5>
                            </div>
                            <DateChange
                                selectedDate={socialSignupSelectedDate}
                                setSelectedDate={setSocialSignupSelectedDate}
                                chartType={socialSignupChartType}
                                setChartType={setSocialSignupChartType}
                                handleDateChange={(e) => setSocialSignupSelectedDate(e.target.value)}
                            />
                            <div className="card-body p-4 pt-0">
                                <LineChart data={socialSignupData} labels={socialSignupLabels} title="소셜 가입 수" color="#ec4899" />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-6 mb-4">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100 bg-light">
                             <div className="card-body d-flex align-items-center justify-content-center text-muted">
                                 <p className="mb-0 italic">추가 지표를 준비 중입니다...</p>
                             </div>
                        </div>
                    </div>



                </div>
            </div>
        </AdminLayout>
    );
};

export default Admin;
